/** Pixels with all RGB channels below this are "dark" (floor plan lines) */
const DARK_THRESHOLD = 100

/** Pixels with all RGB channels at or above this are "near-white" */
const WHITE_THRESHOLD = 240

/**
 * Processes floor plan image pixels using a two-pass white halo algorithm.
 *
 * Pass 1 (halo detection): Identifies visible dark pixels (RGB all < 100, alpha > 0)
 * and marks their 8 immediate neighbors as "halo" pixels.
 *
 * Pass 2 (transparency): Makes white/near-white pixels (RGB all >= 240)
 * transparent — EXCEPT halo pixels, which stay opaque white to provide a 1px
 * contrast border around dark floor plan lines.
 *
 * Shared between Wireless Config and Map View floor plan rendering.
 *
 * @param {ImageData} imageData - Canvas ImageData with pixel RGBA values
 * @param {Object} [options]
 * @param {boolean} [options.useHalo=true] - When true, uses two-pass halo algorithm
 *   that preserves a 1px white border around dark lines. When false, makes all
 *   white pixels transparent (simple single-pass, used by Map View).
 * @returns {ImageData} Modified ImageData with transparent white pixels
 */
export function processFloorPlanPixels(imageData, { useHalo = true } = {}) {
  const { data, width, height } = imageData
  const pixelCount = width * height

  // Pass 1: Build halo mask — mark neighbors of dark pixels (only when halo enabled)
  let isHalo
  if (useHalo) {
    isHalo = new Uint8Array(pixelCount)

    for (let i = 0; i < pixelCount; i++) {
      const offset = i * 4
      const r = data[offset]
      const g = data[offset + 1]
      const b = data[offset + 2]

      if (data[offset + 3] > 0 && r < DARK_THRESHOLD && g < DARK_THRESHOLD && b < DARK_THRESHOLD) {
        // Dark pixel — mark its 8 neighbors as halo
        const x = i % width
        const y = Math.floor(i / width)

        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue
            const nx = x + dx
            const ny = y + dy
            if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
              isHalo[ny * width + nx] = 1
            }
          }
        }
      }
    }
  }

  // Pass 2: Make white pixels transparent, except halo pixels (when enabled)
  for (let i = 0; i < pixelCount; i++) {
    const offset = i * 4
    const r = data[offset]
    const g = data[offset + 1]
    const b = data[offset + 2]

    if (r >= WHITE_THRESHOLD && g >= WHITE_THRESHOLD && b >= WHITE_THRESHOLD) {
      if (!useHalo || !isHalo[i]) {
        data[offset + 3] = 0 // Set alpha to 0 (transparent)
      }
    }
  }

  return imageData
}
